import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { defaultEntitiesExportFieldNames, entitiesExportFields } from "~pages/Entities/EntitiesListPage/constants/exportFields.ts";
import useDeleteEntity from "~pages/Entities/EntitiesListPage/hooks/useDeleteEntity.ts";
import { useExportEntities } from "~pages/Entities/EntitiesListPage/hooks/useExportEntities.ts";
import useFetchEntities from "~pages/Entities/EntitiesListPage/hooks/useFetchEntites.ts";
import { useGetEntitiesTableColumns } from "~pages/Entities/EntitiesListPage/hooks/useGetEntitiesTableColumns.tsx";
import { useGetEntitiesTableFilters } from "~pages/Entities/EntitiesListPage/hooks/useGetEntitiesTableFilters.tsx";
import { useImportEntities } from "~pages/Entities/EntitiesListPage/hooks/useImportEntities.ts";
import type { SearchFilterParams } from "~pages/Persons/PersonListPage/types/searchFilterParams.ts";
import { removeEmptyFields } from "~utils/removeEmptyFields.ts";

const EntitiesListPage = () => {
  const { sport, organizationId, customerId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { uiConfiguration } = useSportConfiguration();
  const location = useLocation();
  const dataTableUiConfiguration = uiConfiguration.pages.entities.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.entities.listPage.importConfig;
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const paginationConfig = useHandlePagination(50);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const { mutateAsync: startImporting } = useImportEntities();
  const { mutateAsync: startExport, isPending: isExportPending } = useExportEntities();

  const columns = useGetEntitiesTableColumns();
  const filterConfig = useGetEntitiesTableFilters(
    removeEmptyFields({
      status: location.state?.status ? { value: location.state?.status, label: location.state?.status } : null,
    }),
  );

  const listParams = useMemo<SearchFilterParams>(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      nameFullLocalContains: searchText,
      sortBy: sortBy || "",
      gender: filterConfig.currentFilter.gender?.value,
      ageGroup: filterConfig.currentFilter.ageGroup?.value,
      discipline: filterConfig.currentFilter.discipline?.value,
      standard: filterConfig.currentFilter.standard?.value,
      status: filterConfig.currentFilter.status?.value,
      includeTotalCount: "true",
      include: ["entityGroups"],
    }),
    [paginationConfig, searchText, filterConfig.currentFilter, sortBy],
  );

  const { data, isLoading, refetch } = useFetchEntities(listParams, sortBy !== null);
  const { mutateAsync: deleteEntity, isPending: isDeletePending } = useDeleteEntity();

  const totalCount = data?.totalCount || undefined;

  return (
    <DataTable
      title={formatMessage({ id: "entityListPage.title" })}
      columns={columns}
      handleRefresh={() => {
        refetch();
      }}
      filtersConfig={filterConfig}
      isDataLoading={isLoading}
      rowActions={{
        base: {
          delete: {
            onClick: (row) => deleteEntity(row.entityId).then(() => refetch()),
            loading: isDeletePending,
          },
          view: {
            onClick: (row) => navigate(`/${customerId}/admin/${sport}/${organizationId}/entities/manage/${row.entityId}`),
          },
        },
      }}
      handleAdd={() => navigate(`/${customerId}/admin/${sport}/${organizationId}/entities/manage`)}
      addButtonIntlKey={"entity"}
      rowKey={"entityId"}
      rowNameKey={"nameFullLocal"}
      dataSource={data?.data || []}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.entities`}
      columnSortConfig={{
        sorterLocalStorageKey: `tables.settings.sort.entities`,
        defaultSorter: {
          columnKey: "nameFullLocal",
          order: "ascend",
        },
        onSortChange: setSortBy,
      }}
      searchConfig={{
        handleSearch: setSearchText,
        searchText: searchText,
      }}
      exportConfig={{
        handleExport: startExport,
        loading: isExportPending,
        defaultFields: defaultEntitiesExportFieldNames,
        groupedFields: entitiesExportFields,
        localStorageKey: "export.persons",
      }}
      importConfig={{
        ...dataTableImportConfiguration,
        handleImport: startImporting,
      }}
      permissionNames={[PermissionNames.ENTITY]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, total: totalCount }}
    />
  );
};

export default EntitiesListPage;
